import AddCircle from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { SyntheticEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { CHECKBOX, FREE_TEXT, horizontalScrollTableStyle } from '../../constants';
import { ColumnProps } from '../../models/global';
import { State } from '../../models/state';
import Dropdown from '../FormComponents/Dropdown';
import TextField from '../FormComponents/TextField';
import './SurveyForm.scss';

interface TableComponentProps {
  handleTableColumnName: (value: string, colId: string) => void;
  addColumn: (columnType: string) => void;
  removeColumn: (colId: string) => void;
  table: {
    id: string;
    columns: [] | ColumnProps[];
  };
  language: string;
  invalidFields: string[];
  readonly?: boolean;
  blockId: string;
}

function TableComponent(props: TableComponentProps) {
  const { handleTableColumnName, addColumn, removeColumn, table, language, invalidFields, readonly, blockId } = props;
  const [columnType, setColumnType] = useState('');
  const { columnTypesTable } = useSelector((state: State) => state.survey);

  const getField = (type: string) => {
    switch (type) {
      case CHECKBOX:
        return <Checkbox disabled />;
      case FREE_TEXT:
        return <TextField multiline id='answer' label='Answer...' variant='standard' disabled sx={{ ml: 2 }} />;
    }
  };

  const circleErrorStyle = {
    border: '1px solid red',
    borderRadius: '15px',
  };

  return (
    !!table && (
      <Box sx={{ mt: 5 }} className='table-container' style={{ pointerEvents: 'visible' }}>
        <Box width={230} className='flex-center column-type' style={readonly ? { pointerEvents: 'none' } : {}}>
          <Dropdown
            value={columnType}
            label='Column Type'
            options={columnTypesTable}
            onChange={(_: SyntheticEvent, value: string) => setColumnType(value)}
            error={!columnType && invalidFields.includes(`COLUMN_LENGTH_${table.id}_${blockId}`)}
            required
          />
          {!!columnType && (
            <AddCircle
              htmlColor='green'
              onClick={() => addColumn(columnType)}
              style={invalidFields.includes(`COLUMN_LENGTH_${table.id}_${blockId}`) ? circleErrorStyle : {}}
            />
          )}
        </Box>
        <TableContainer component={Paper} sx={{ ...horizontalScrollTableStyle, mt: 2.5 }}>
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead style={readonly ? { pointerEvents: 'none' } : {}}>
              <TableRow>
                {table?.columns.map((tableCol: ColumnProps, columnIndex: number) => (
                  <TableCell key={tableCol.id}>
                    <Box>
                      <TextField
                        multiline
                        variant='standard'
                        value={language === 'English' ? tableCol.name : `${tableCol[`name${language}`] || ''}`}
                        onChange={e => handleTableColumnName(e.target.value, tableCol.id)}
                        error={invalidFields.includes(`COLUMN_${columnIndex}_${table.id}_${blockId}`)}
                      />
                      {language !== 'English' && (
                        <Typography className='english-value'>English: {tableCol.name || 'N/A'}</Typography>
                      )}
                    </Box>
                    <RemoveCircleIcon htmlColor='red' onClick={() => removeColumn(tableCol.id)} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody style={readonly ? { pointerEvents: 'none' } : {}}>
              <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                {table?.columns.map((column: ColumnProps, index: number) => (
                  <TableCell component='td' scope='row' key={index}>
                    {getField(column.type)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  );
}

export default TableComponent;
